import React from 'react';
import { AgencyState, InputFieldState } from '../types';
import IsProcedureExecutorForm from '../IsProcedureExecutorForm';
import IdentificationNumberForm from '../IdentificationNumberForm';
import AddressForm from '../AddressForm';
import NameForm from '../NameForm';
import RepresentativeForm from '../RepresentativeForm';

/**
 * AgencyFormコンポーネント用のPropsです。
 * @interface AgencyProps
 */
interface AgencyProps {
  /**
   * 索引
   * @type {number}
   * @memberof AgencyProps
   */
  index: number;

  /**
   * 代理人
   * @type {string}
   * @memberof AgencyProps
   */
  agency: AgencyState;

  /**
   * 代理人を設定する関数
   * @memberof AgencyProps
   */
  onChange: (index: number, updatedAgency: AgencyState) => void;
}

/**
 * 代理人フォームのコンポーネントです。
 * @param {number} props.index - 索引
 * @param {string} props.agency - 代理人
 * @param {function} props.onChange - 代理人情報が変更されたときに呼ばれるコールバック関数
 * @returns {JSX.Element} - 代理人フォームのJSX要素
 */
const AgencyForm: React.FC<AgencyProps> = ({ index, agency, onChange }) => {

  /**
   * 識別番号を更新する関数です。
   * @param {string} identificationNumber - 識別番号
   * @returns {void}
   */
  const updateIdentificationNumber = (identificationNumber: InputFieldState): void => {
    onChange(index, 
      {
        isProcedureExecutor: agency.isProcedureExecutor,
        identificationNumber: {
          value: identificationNumber.value,
          errorMessage: identificationNumber.errorMessage
        },
        address: agency.address,
        name: agency.name,
        representative: agency.representative
      }
    );
  };

  /**
   * 手続実行者フラグを更新する関数です。
   * @param {string} isProcedureExecutor - 手続実行者フラグ
   * @returns {void}
   */
  const updateIsProcedureExecutor = (isProcedureExecutor: boolean): void => {
    onChange(index, 
      {
        isProcedureExecutor: isProcedureExecutor,
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: agency.name,
        representative: agency.representative
      }
    );
  };

  /**
   * 住所又は居所を更新する関数です。
   * @param {InputFieldState} address - 住所又は居所
   * @returns {void}
   */
  const updateAddress = (address: InputFieldState): void => {
    onChange(index, 
      {
        isProcedureExecutor: agency.isProcedureExecutor,
        identificationNumber: agency.identificationNumber,
        address: address,
        name: agency.name,
        representative: agency.representative
      }
    );
  };

  /**
   * 氏名又は名称を更新する関数です。
   * @param {InputFieldState} name - 氏名又は名称
   * @returns {void}
   */
  const updateName = (name: InputFieldState): void => {
    onChange(index, 
      {
        isProcedureExecutor: agency.isProcedureExecutor,
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: name,
        representative: agency.representative
      }
    );
  };

  /**
   * 代表者を更新する関数です。
   * @param {InputFieldState} representative - 代表者
   * @returns {void}
   */
  const updateRepresentative = (representative: InputFieldState): void => {
    onChange(index, 
      {
        isProcedureExecutor: agency.isProcedureExecutor,
        identificationNumber: agency.identificationNumber,
        address: agency.address,
        name: agency.name,
        representative: representative
      }
    );

  };

  return (
    <>
      <div className="ms-lg-5">
        <IsProcedureExecutorForm
          index={index}
          isChecked={agency.isProcedureExecutor}
          onChange={updateIsProcedureExecutor}
        />
        <IdentificationNumberForm
          identificationNumber={agency.identificationNumber}
          isAgency={true}
          onChange={updateIdentificationNumber}
        />
        <AddressForm
          address={agency.address}
          isAgency={true}
          onChange={updateAddress}
        />
        <NameForm
          name={agency.name}
          isAgency={true}
          onChange={updateName}
        />
        <RepresentativeForm
          representative={agency.representative}
          name={agency.name}
          isProcedureExecutor={agency.isProcedureExecutor}
          onChange={updateRepresentative}
        />
      </div>
    </>
  );
}

export default AgencyForm;